import React, { useEffect, useState } from 'react';
import {
  Button,
  Text,
  VStack,
  HStack,
  LoadingSpinner,
  Card,
  Highlight,
  ScrollView,
  IconButton,
} from '@adc/parallax-component-library';
import {
  IconBannersInfo,
  IconBannersWarning,
  IconCheckCircle,
  IconDownload,
} from '@adc/parallax-icons';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import { DownloadStatus } from '../../constants';
import { DownloadState } from '../../hooks/useStudyDownload';
import { ProgressBar } from '../progressBar/ProgressBar';
import { ConfirmationModal } from './ConfirmationModal';
import './Modal.scss';

export interface DownloadModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  studyName: string;
}

export type DownloadModalProps = DownloadModalBaseProps & DownloadState;

/**
 * Modal for showing export progress and providing download link for study data.
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Callback function to close the modal
 * @param {string} studyName - Name of the study being exported
 * @param {DownloadState} downloadState - State of the download process, including status, progress, and download URLs
 * @returns {React.JSX.Element}
 */
export default function DownloadModal({
  isOpen,
  onClose,
  studyName,
  status,
  progress,
  jobKey,
  downloadUrl,
  filename,
  checksumDownloadUrl,
  checksumFilename,
  errorMessage,
}: DownloadModalProps) {
  const isDownloadActive =
    status === DownloadStatus.PREPARING ||
    status === DownloadStatus.PROCESSING ||
    status === DownloadStatus.ACQUIRING ||
    status === DownloadStatus.RECONNECTING;
  const isDataFileAvailable =
    status === DownloadStatus.COMPLETED && !!downloadUrl && !!filename;
  const isChecksumFileAvailable = !!checksumDownloadUrl && !!checksumFilename;

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // Prevent browser unload during active exports
  // TODO: do we want to cancel the active job via the API?
  useEffect(() => {
    if (!isDownloadActive) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return `${i18n.t('Modal_Download.active_body')}`;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDownloadActive]);

  const handleCloseAttempt = () => {
    if (isDownloadActive) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const triggerFileDownload = (url: string, filename: string) => {
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadClick = () => {
    if (isDataFileAvailable) {
      setIsButtonDisabled(true);

      try {
        triggerFileDownload(downloadUrl, filename);

        if (isChecksumFileAvailable) {
          triggerFileDownload(checksumDownloadUrl, checksumFilename);
        }
      } finally {
        setIsButtonDisabled(false);
      }
    }
  };

  const renderStatusBanner = () => {
    switch (status) {
      case DownloadStatus.ACQUIRING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.acquiring_title')}
            body={i18n.t('Modal_Download.acquiring_body')}
            testID="download-acquiring-banner"
          />
        );
      case DownloadStatus.FAILED:
        return (
          <Highlight
            variant="danger"
            iconStart={
              <IconBannersWarning
                color="$neutral.0"
                accentColor="$support.danger.default"
              />
            }
            title={i18n.t('Modal_Download.failed_title')}
            body={errorMessage || i18n.t('Modal_Download.error_generic_failed')}
            testID="download-failed-banner"
          />
        );
      case DownloadStatus.PREPARING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.preparing_title')}
            body={i18n.t('Modal_Download.active_body')}
            testID="download-preparing-banner"
          />
        );
      case DownloadStatus.PROCESSING:
      case DownloadStatus.RECONNECTING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.processing_title')}
            body={i18n.t('Modal_Download.active_body')}
            testID="download-processing-banner"
          />
        );
      case DownloadStatus.COMPLETED:
        return (
          <Highlight
            variant="success"
            iconStart={
              <IconCheckCircle
                color="$neutral.0"
                accentColor="$support.positive.default"
              />
            }
            title={i18n.t('Modal_Download.completed_description')}
            body={i18n.t('Modal_Download.completed_body')}
            testID="download-completed-banner"
          />
        );
      default:
        return null;
    }
  };

  const renderDownloadSection = () => {
    return isDataFileAvailable ? (
      <VStack gap="$2">
        <Text type="$bodyBase.strong">
          {i18n.t('Modal_Download.exported_file_label')}
        </Text>
        <Card>
          <HStack gap="$2" alignItems="center" justifyContent="space-between">
            <VStack gap="$1">
              <Text type="$bodySmall.default" testID="download-filename">
                {filename}
              </Text>
              {isChecksumFileAvailable ? (
                <Text
                  type="$bodySmall.default"
                  testID="download-checksum-filename"
                >
                  {checksumFilename}
                </Text>
              ) : null}
            </VStack>
            <IconButton
              variant="tertiary"
              aria-label={i18n.t('Modal_Download.download_export_file')}
              onPress={handleDownloadClick}
              disabled={isButtonDisabled}
              testID="download-modal-download-button"
            >
              <IconDownload />
            </IconButton>
          </HStack>
        </Card>
      </VStack>
    ) : null;
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="50%">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">{i18n.t('Modal_Download.title')}</Text>
            </Dialog.Title>
          </VStack>

          <ScrollView flex={1} minHeight={0} marginVertical="$4">
            <VStack gap="$2">
              <VStack gap="$1">
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_Download.study_label')}
                </Text>
                <Text type="$bodyBase.default" testID="download-study-name">
                  {studyName}
                </Text>
              </VStack>

              {renderStatusBanner()}

              {jobKey && status === DownloadStatus.FAILED ? (
                <Text type="$bodySmall.default" testID="download-job-key">
                  {i18n.t('Modal_Download.job_key', { jobKey })}
                </Text>
              ) : null}

              {status !== DownloadStatus.IDLE && progress !== undefined ? (
                <VStack gap="$3" alignItems="center">
                  {status === DownloadStatus.PREPARING ||
                  status === DownloadStatus.ACQUIRING ? (
                    // Contain the rotating square so its corners cannot resize
                    // the scrollable area around the circular spinner.
                    <VStack
                      width={50}
                      height={50}
                      flexShrink={0}
                      overflow="hidden"
                    >
                      <LoadingSpinner testID="download-loading" />
                    </VStack>
                  ) : null}

                  {status !== DownloadStatus.ACQUIRING ? (
                    <VStack gap="$2" width="100%">
                      <ProgressBar
                        progress={progress}
                        color={
                          status === DownloadStatus.FAILED
                            ? '$neutral.40'
                            : '$abbott.lightBlue.default'
                        }
                        testId="download-progress-bar"
                      />
                      <Text
                        textAlign="center"
                        type="$bodySmall.default"
                        testID="download-progress-text"
                      >
                        {`${Math.round(progress)}${i18n.t('Modal_Download.progress_complete')}`}
                      </Text>
                    </VStack>
                  ) : null}
                </VStack>
              ) : null}

              {renderDownloadSection()}
            </VStack>
          </ScrollView>
          <HStack justifyContent="flex-end" gap="$3" flexShrink={0}>
            <Button
              variant="secondary"
              size="medium"
              label={
                status === DownloadStatus.FAILED ||
                status === DownloadStatus.COMPLETED
                  ? i18n.t('Common.close')
                  : i18n.t('Common.cancel')
              }
              onPress={handleCloseAttempt}
              testID="download-modal-close-button"
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>

      <ConfirmationModal
        isOpen={showConfirmClose}
        title={i18n.t('Modal_Download.confirm_close_title')}
        description={i18n.t('Modal_Download.active_body')}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        showCancelButton={true}
      />
    </Dialog>
  );
}
